import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Slide,
  Stack,
  Table,
  TableBody as MuiTableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useGetClinicWarehouseItems } from "../../../hooks/useGetClinicWarehouseItems";
import {
  Close,
  Clear,
  Inventory2Outlined,
  SearchOutlined,
} from "@mui/icons-material";
import useSubmit from "../../../../../../../master/species/hooks/useSubmit";
import { ASSIGN_RESOURCE_TO_SERVICE } from "../../../../../../../../enums/QueryKeys";
import logger from "../../../../../../../../utils/logger";
import { useFetch } from "../../../../../../../../hooks/Fetching";
import { ASSIGN_RESOURCE_TO_SERVICE_API, GET_RESOURCES } from "../../../../../../../../enums/ApiPaths";
import { useTranslation } from "react-i18next";
import { trimText } from "../../../../../../../../utils/Functions";

const LOADER_DELAY_MS = 1500;
const TABLE_HEIGHT = 400;

const SlideUpTransition = React.forwardRef(
  function SlideUpTransition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  },
);
export default function ServiceResources({ price, clinicId }) {
  const url = GET_RESOURCES + price?.id;
  const { data, isLoading } = useFetch(
    url,
    null,
    ASSIGN_RESOURCE_TO_SERVICE + price?.id,
    price?.id,
  );
  if (isLoading) return <LinearProgress />;
  return (
    <WarehouseItemSelectorDialog
      key={price?.id}
      isDialogOnSmallScreen
      price={price}
      clinicId={clinicId}
      existingResources={data}
    />
  );
}

export function WarehouseItemSelectorDialog({
  isDialogOnSmallScreen = false,
  price,
  clinicId,
  existingResources = [],
}) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const useFullScreenDialog = isDialogOnSmallScreen && isSmallScreen;

  const url = ASSIGN_RESOURCE_TO_SERVICE_API + clinicId;
  const submission = useSubmit(
    null,
    url,
    ASSIGN_RESOURCE_TO_SERVICE + price?.id,
  );

  const methods = useForm({
    defaultValues: {
      selectedItems: new Map(existingResources.map((item) => [item.id, item])),
    },
  });

  const onSubmit = (formValues) => {
    const payload = {
      clinicServiceId: price.id,
      listOfResourceIds: Array.from(formValues.selectedItems.keys()),
    };
    try {
      submission.submitCallback(payload);
    } catch (err) {
      logger.error(err);
    }
  };

  return (
    <FormProvider {...methods}>
      {useFullScreenDialog ? (
        <FullScreenDialogVersion onSubmit={methods.handleSubmit(onSubmit)} clinicId={clinicId} />
      ) : (
        <Stack spacing={2}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 3 }}
              alignItems="flex-start"
              sx={{ width: "100%" }}
            >
              <WarehouseItemsTable clinicId={clinicId} />
              <SelectedItemsPanel />
            </Stack>
            <FormFooter />
          </form>
        </Stack>
      )}
    </FormProvider>
  );
}

function FullScreenDialogVersion({ onSubmit, clinicId }) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { t } = useTranslation();

  return (
    <>
      <Button variant="contained" onClick={() => setDialogOpen(true)}>
        {t("SelectResources")}
      </Button>
      <Dialog
        fullScreen
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        TransitionComponent={SlideUpTransition}
      >
        <AppBar
          position="relative"
          color="default"
          elevation={0}
          sx={{ borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => setDialogOpen(false)}
              aria-label="close"
            >
              <Close />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 1, flex: 1 }}>
              {t("SelectResources")}
            </Typography>
          </Toolbar>
        </AppBar>

        <form
          onSubmit={onSubmit}
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: "auto" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 3 }}
              alignItems="flex-start"
              sx={{ width: "100%" }}
            >
              <WarehouseItemsTable clinicId={clinicId} />
              <SelectedItemsPanel />
            </Stack>
          </Box>

          <FormFooter useFullScreenDialog />
        </form>
      </Dialog>
    </>
  );
}

function WarehouseItemsTable({ clinicId }) {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslation();
  const selectedItems = watch("selectedItems");

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchInput, setSearchInput] = React.useState("");
  const [showLoader, setShowLoader] = React.useState(false);

  const {
    data,
    isLoading,
    isFetching: isItemsFetching,
  } = useGetClinicWarehouseItems(clinicId, {
    categoryId: null,
    supplierId: null,
    search: searchInput,
    sort: null,
    page,
    size: rowsPerPage,
  });

  React.useEffect(() => {
    if (!isItemsFetching) {
      setShowLoader(false);
      return;
    }
    const timer = setTimeout(() => setShowLoader(true), LOADER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isItemsFetching]);

  const items = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;

  const handleToggleItem = (item) => {
    const next = new Map(selectedItems);
    next.has(item.id) ? next.delete(item.id) : next.set(item.id, item);
    setValue("selectedItems", next, { shouldDirty: true });
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Paper
      variant="outlined"
      sx={{ flex: 2, minWidth: 0, width: "100%", borderRadius: 2 }}
    >
      <Box sx={{ p: 2, pb: 0 }}>
        <TextField
          fullWidth
          placeholder="Search items…"
          variant="outlined"
          size="small"
          value={searchInput}
          onChange={handleSearchChange}
          inputProps={{ "data-testid": "service.resources.search-input" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined
                  fontSize="small"
                  sx={{ color: "text.disabled" }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
      </Box>

      <Box sx={{ position: "relative", minHeight: 4 }}>
        {showLoader && (
          <LinearProgress
            sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
          />
        )}
      </Box>

      <TableContainer sx={{ minHeight: TABLE_HEIGHT, maxHeight: TABLE_HEIGHT }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 600 }}>{t("Title")}</TableCell>
            </TableRow>
          </TableHead>
          <MuiTableBody>
            {items.map((item) => {
              const isChecked = selectedItems.has(item.id);
              return (
                <TableRow
                  key={item.id}
                  hover
                  selected={isChecked}
                  onClick={() => handleToggleItem(item)}
                  sx={{
                    cursor: "pointer",
                    "&.Mui-selected": { bgcolor: "action.selected" },
                  }}
                  data-testid={`service.resources.item.${item.id}`}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isChecked}
                      onChange={() => handleToggleItem(item)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>{item.title}</TableCell>
                </TableRow>
              );
            })}
            {!isLoading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 6 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Inventory2Outlined
                      sx={{ fontSize: 32, color: "text.disabled" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t("NoItemsMatchYourSearch")}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </MuiTableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        labelRowsPerPage={t("RowsPerPage")}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      />
    </Paper>
  );
}

export function SelectedItemsPanel({showQuantity = false}) {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslation();
  const selectedItems = watch("selectedItems") ?? [];
  const items = Array.from(selectedItems.values());

  const handleRemoveItem = (itemId) => {
    const next = new Map(selectedItems);
    next.delete(itemId);
    setValue("selectedItems", next, { shouldDirty: true });
  };

  const handleClearAll = () =>
    setValue("selectedItems", new Map(), { shouldDirty: true });

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: { xs: "100%", sm: 260 },
        width: { xs: "100%", sm: "auto" },
        minHeight: 0,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2">{t("Selected")}</Typography>
          <Chip
            label={selectedItems?.size}
            size="small"
            color={selectedItems?.size > 0 ? "primary" : "default"}
            sx={{ height: 20, minWidth: 20, fontWeight: 600 }}
            data-testid="service.resources.selected-panel.count-chip"
          />
        </Stack>
        {selectedItems.size > 0 && (
          <IconButton
            size="small"
            title={t("ClearAll")}
            onClick={handleClearAll}
            data-testid="service.resources.selected-panel.clear-all-button"
          >
            <Clear fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack
        spacing={1}
        sx={{
          p: 2,
          maxHeight: TABLE_HEIGHT + 55,
          minHeight: TABLE_HEIGHT + 55,
          overflowY: "auto",
        }}
      >
        {items.map((item) => (
          <Stack
            key={item.id}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ px: 1, py: 0.5, borderRadius: 1.5, bgcolor: "action.hover" }}
            data-testid={`service.resources.selected-panel.item.${item.id}`}
          >
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {trimText(item.title)}
            </Typography>
            {showQuantity && (
              <Chip
                label={item?.quantity}
                data-testid={`service.resources.selected-panel.item.${item.id}.quantity-chip`}
              />
            )}
            <IconButton
              size="small"
              onClick={() => handleRemoveItem(item.id)}
              data-testid={`service.resources.selected-panel.item.${item.id}.remove-button`}
            >
              <Clear fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {selectedItems.size === 0 && (
          <Stack
            alignItems="center"
            spacing={1}
            sx={{ py: 4 }}
            data-testid="service.resources.selected-panel.empty-state"
          >
            <Inventory2Outlined sx={{ fontSize: 28, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary" align="center">
              {t("CheckItemsFromTableInfo")}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function FormFooter({ useFullScreenDialog = false }) {
  const { watch } = useFormContext();
  const selectedItems = watch("selectedItems");
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      sx={{
        p: 2,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        flexShrink: 0,
      }}
    >
      <Button
        variant="contained"
        type="submit"
        fullWidth={useFullScreenDialog}
        data-testid="service.resources.save-button"
      >
        {t("AssingItemToServiceButton")}
      </Button>
    </Stack>
  );
}
